import type {
  RockPaperScissorsEvent,
  RockPaperScissorsGesture,
  RockPaperScissorsPublicState,
} from "@topzle/game-rock-paper-scissors";
import type { ActiveRoomSnapshot } from "@topzle/types";
import {
  Bot,
  Camera,
  Check,
  LoaderCircle,
  LockKeyhole,
  ScanLine,
  Timer,
  VideoOff,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  recognizeRockPaperScissorsFrame,
  shouldUseMainThreadMediaPipe,
  warmRockPaperScissorsRecognizer,
} from "../rpsGestureRecognizer";

const gesturePresentation: Record<
  RockPaperScissorsGesture,
  { icon: string; label: string; mediaPipe: string }
> = {
  rock: { icon: "✊", label: "Rock", mediaPipe: "Closed fist" },
  paper: { icon: "✋", label: "Paper", mediaPipe: "Open palm" },
  scissors: { icon: "✌️", label: "Scissors", mediaPipe: "Victory sign" },
};

function playerName(
  playerId: string,
  participantId: string,
  participants: ActiveRoomSnapshot["participants"],
) {
  if (playerId === participantId) return "You";
  if (playerId.startsWith("ai:")) return "Topzle AI";
  return participants.find((participant) => participant.id === playerId)?.displayName ?? "Player";
}

function CameraFeed({
  stream,
  videoRef,
  muted,
  label,
}: {
  stream: MediaStream | null;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  muted: boolean;
  label: string;
}) {
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = muted;
    video.playsInline = true;
    video.autoplay = true;
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    video.disablePictureInPicture = true;
    video.srcObject = stream;
    const play = () => {
      if (stream && video.paused) void video.play().catch(() => undefined);
    };
    const tracks = stream?.getVideoTracks() ?? [];
    for (const track of tracks) track.addEventListener("unmute", play);
    video.addEventListener("loadedmetadata", play);
    video.addEventListener("canplay", play);
    play();
    return () => {
      for (const track of tracks) track.removeEventListener("unmute", play);
      video.removeEventListener("loadedmetadata", play);
      video.removeEventListener("canplay", play);
      if (video.srcObject === stream) video.srcObject = null;
    };
  }, [muted, stream, videoRef]);
  return stream ? (
    <video ref={videoRef} autoPlay playsInline muted={muted} aria-label={label} />
  ) : (
    <div className="rps-no-camera" role="status">
      <VideoOff />
      <span>Camera not connected</span>
    </div>
  );
}

function snapshotVideo(video: HTMLVideoElement | null): string | null {
  if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return null;
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(320, video.videoWidth);
  canvas.height = Math.max(240, video.videoHeight);
  const context = canvas.getContext("2d");
  if (!context) return null;
  context.translate(canvas.width, 0);
  context.scale(-1, 1);
  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", 0.76);
}

async function recognitionFrame(video: HTMLVideoElement): Promise<ImageBitmap | ImageData> {
  if (!shouldUseMainThreadMediaPipe() && typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(video);
    } catch {
      // iPhone Safari can display a MediaStream while rejecting the video overload.
      // Fall through to an ImageData frame, which MediaPipe also accepts.
    }
  }
  const width = Math.max(320, video.videoWidth || 0);
  const height = Math.max(240, video.videoHeight || 0);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("This browser could not read the camera frame");
  context.drawImage(video, 0, 0, width, height);
  return context.getImageData(0, 0, width, height);
}

export function RockPaperScissorsBoard({
  state,
  participantId,
  participants,
  localVideoStream,
  remoteMediaStreams,
  cameraPending,
  pending,
  serverError,
  playAgain,
  onEnableCamera,
  onAction,
}: {
  state: RockPaperScissorsPublicState;
  participantId: string;
  participants: ActiveRoomSnapshot["participants"];
  localVideoStream: MediaStream | null;
  remoteMediaStreams: Map<string, MediaStream>;
  cameraPending: boolean;
  pending: boolean;
  serverError: string | null;
  playAgain: React.ReactNode;
  onEnableCamera: () => void;
  onAction: (action: RockPaperScissorsEvent) => boolean;
}) {
  const [now, setNow] = useState(Date.now());
  const [modelStatus, setModelStatus] = useState<"loading" | "ready" | "error">("loading");
  const [recognitionHint, setRecognitionHint] = useState("Show one hand clearly in the frame.");
  const [recognizing, setRecognizing] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [snapshots, setSnapshots] = useState<Record<string, string>>({});
  const [landmarks, setLandmarks] = useState<Array<{ x: number; y: number }>>([]);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const rivalVideoRef = useRef<HTMLVideoElement>(null);
  const lastRecognitionAttempt = useRef(0);
  const rivalSnapshotRound = useRef(0);
  const autoReadySent = useRef(false);
  const player = state.players.includes(participantId);
  const leftId = player ? participantId : (state.players[0] ?? "");
  const rivalId = player
    ? (state.players.find((playerId) => playerId !== participantId) ?? state.players[1])
    : state.players[1];
  const leftStream = player
    ? localVideoStream
    : leftId.startsWith("ai:")
      ? null
      : (remoteMediaStreams.get(leftId) ?? null);
  const rivalStream =
    rivalId && !rivalId.startsWith("ai:") ? (remoteMediaStreams.get(rivalId) ?? null) : null;
  const ownThrow = state.throws[leftId];
  const rivalThrow = rivalId ? state.throws[rivalId] : undefined;
  const cameraReady = Boolean(
    localVideoStream?.getVideoTracks().some((track) => track.readyState === "live"),
  );

  useEffect(() => {
    if (cameraReady || manualMode) setCameraDialogOpen(false);
  }, [cameraReady, manualMode]);

  const ready = state.readyIds.includes(participantId);
  const millisecondsUntilCapture = (state.countdownEndsAt ?? now) - now;
  const captureWindowOpen =
    state.phase === "countdown" &&
    millisecondsUntilCapture <= 900 &&
    now <= (state.captureClosesAt ?? now);
  const countdown =
    state.phase === "countdown"
      ? millisecondsUntilCapture > 0
        ? String(Math.max(1, Math.ceil(millisecondsUntilCapture / 1000)))
        : "SHOW!"
      : null;
  const nextRoundSeconds =
    state.phase === "reveal" && state.nextRoundAt !== null
      ? Math.max(0, Math.ceil((state.nextRoundAt - now) / 1000))
      : null;
  const scores = state.players.map((playerId) => ({
    playerId,
    name: playerName(playerId, participantId, participants),
    score: state.scores[playerId] ?? 0,
  }));

  useEffect(() => {
    if (!player) {
      setModelStatus("ready");
      return;
    }
    let active = true;
    setModelStatus("loading");
    void warmRockPaperScissorsRecognizer()
      .then(() => {
        if (active) setModelStatus("ready");
      })
      .catch((error) => {
        if (!active) return;
        console.error("RPS on-device hand recognition could not initialize", error);
        setModelStatus("error");
        setManualMode(true);
        setRecognitionHint("MediaPipe could not load. Use the accessible manual controls.");
      });
    return () => {
      active = false;
    };
  }, [player]);

  // The round number is the reset boundary for browser-local captures.
  // biome-ignore lint/correctness/useExhaustiveDependencies: state.round intentionally triggers this reset.
  useEffect(() => {
    setSnapshots({});
    setLandmarks([]);
    setRecognitionHint("Show one hand clearly in the frame.");
    lastRecognitionAttempt.current = 0;
    rivalSnapshotRound.current = 0;
    autoReadySent.current = false;
  }, [state.round]);

  useEffect(() => {
    if (state.phase !== "countdown" && state.phase !== "reveal") return;
    setNow(Date.now());
    const interval = window.setInterval(() => setNow(Date.now()), 80);
    return () => window.clearInterval(interval);
  }, [state.phase]);

  useEffect(() => {
    if (state.phase === "finished") autoReadySent.current = false;
  }, [state.phase]);

  useEffect(() => {
    const canStartAutomatically =
      player &&
      state.phase === "ready" &&
      !ready &&
      !pending &&
      !cameraPending &&
      (manualMode || (cameraReady && modelStatus === "ready"));
    if (!canStartAutomatically) return;
    const tryReady = () => {
      if (!autoReadySent.current)
        autoReadySent.current = onAction({
          type: "ready",
        });
    };
    const timeout = window.setTimeout(tryReady, 350);
    const retry = window.setInterval(tryReady, 1_500);
    return () => {
      window.clearTimeout(timeout);
      window.clearInterval(retry);
    };
  }, [
    cameraPending,
    cameraReady,
    manualMode,
    modelStatus,
    onAction,
    pending,
    player,
    ready,
    state.phase,
  ]);

  useEffect(() => {
    if (
      state.phase !== "countdown" ||
      millisecondsUntilCapture > 100 ||
      rivalSnapshotRound.current === state.round
    )
      return;
    if (!player) {
      const leftSnapshot = snapshotVideo(localVideoRef.current);
      if (leftSnapshot && leftId) setSnapshots((value) => ({ ...value, [leftId]: leftSnapshot }));
    }
    const snapshot = snapshotVideo(rivalVideoRef.current);
    if (snapshot && rivalId) setSnapshots((value) => ({ ...value, [rivalId]: snapshot }));
    rivalSnapshotRound.current = state.round;
  }, [leftId, millisecondsUntilCapture, player, rivalId, state.phase, state.round]);

  useEffect(() => {
    if (
      !player ||
      manualMode ||
      modelStatus !== "ready" ||
      state.phase !== "countdown" ||
      millisecondsUntilCapture > 100 ||
      now > (state.captureClosesAt ?? 0) ||
      ownThrow?.locked ||
      pending ||
      recognizing ||
      now - lastRecognitionAttempt.current < 320
    )
      return;
    const video = localVideoRef.current;
    if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      setRecognitionHint("Your camera is still warming up. Hold your hand steady.");
      return;
    }
    lastRecognitionAttempt.current = now;
    setRecognizing(true);
    void recognitionFrame(video)
      .then(async (image) => {
        const recognition = await recognizeRockPaperScissorsFrame(image);
        setLandmarks(recognition.landmarks);
        if (!recognition.gesture) {
          setRecognitionHint("Use a closed fist, open palm, or a clear two-finger V.");
          return;
        }
        const snapshot = snapshotVideo(video);
        if (snapshot)
          setSnapshots((value) => ({
            ...value,
            [participantId]: snapshot,
          }));
        setRecognitionHint(
          `${gesturePresentation[recognition.gesture].label} detected · ${Math.round(
            recognition.confidence * 100,
          )}%`,
        );
        onAction({
          type: "throw",
          gesture: recognition.gesture,
          confidence: recognition.confidence,
          source: "camera",
        });
      })
      .catch((error) => {
        setRecognitionHint(
          error instanceof Error
            ? error.message
            : "MediaPipe could not read this frame. Hold steady and retry.",
        );
      })
      .finally(() => setRecognizing(false));
  }, [
    manualMode,
    millisecondsUntilCapture,
    modelStatus,
    now,
    onAction,
    ownThrow?.locked,
    participantId,
    pending,
    player,
    recognizing,
    state.captureClosesAt,
    state.phase,
  ]);

  const status = useMemo(() => {
    if (!player) {
      if (state.phase === "ready") return "The players are getting camera ready";
      if (state.phase === "countdown") return "Live throw in progress";
    }
    if (state.phase === "ready")
      return ready
        ? `Ready · waiting for ${playerName(rivalId ?? "", participantId, participants)}`
        : "Camera check · get your hand in frame";
    if (state.phase === "countdown")
      return ownThrow?.locked ? "Throw locked · waiting for the reveal" : "Rock · paper · scissors";
    if (state.roundReason === "draw") return "Same throw · this round is a draw";
    if (state.roundReason === "timeout")
      return state.roundWinnerId === participantId
        ? "You win · your rival missed the capture"
        : state.roundWinnerId
          ? `${playerName(state.roundWinnerId, participantId, participants)} wins on capture`
          : "No hands captured · round draw";
    if (state.roundWinnerId === participantId) return "You take the round!";
    if (state.roundWinnerId)
      return `${playerName(state.roundWinnerId, participantId, participants)} takes the round`;
    return state.phase === "finished" ? "Match complete" : "Throws revealed";
  }, [
    ownThrow?.locked,
    participantId,
    participants,
    player,
    ready,
    rivalId,
    state.phase,
    state.roundReason,
    state.roundWinnerId,
  ]);

  const submitManualThrow = (gesture: RockPaperScissorsGesture) => {
    if (!captureWindowOpen || ownThrow?.locked) return;
    setRecognitionHint(`${gesturePresentation[gesture].label} selected manually.`);
    onAction({ type: "throw", gesture, confidence: 1, source: "manual" });
  };

  return (
    <div className={`active-game rps-game phase-${state.phase}`}>
      <header className="rps-status">
        <div>
          <span>LIVE RPS · ROUND {String(state.round).padStart(2, "0")}</span>
          <strong>{status}</strong>
        </div>
        <section className="rps-scoreboard" aria-label="Match score">
          {scores.map((entry) => (
            <span className={entry.playerId === participantId ? "you" : ""} key={entry.playerId}>
              <small>{entry.name}</small>
              <b>{entry.score}</b>
            </span>
          ))}
          <i>FIRST TO {state.targetScore}</i>
        </section>
      </header>

      <section className="rps-arena">
        <article
          className={`rps-player-card local ${state.roundWinnerId === leftId ? "round-winner" : ""}`}
        >
          <div className="rps-player-label">
            <span>{playerName(leftId, participantId, participants)}</span>
            {ownThrow?.locked && state.phase === "countdown" && (
              <b>
                <LockKeyhole /> Locked
              </b>
            )}
          </div>
          <div className="rps-camera-stage">
            {snapshots[leftId] && (state.phase === "reveal" || state.phase === "finished") ? (
              <img
                src={snapshots[leftId]}
                alt={`${playerName(leftId, participantId, participants)}'s locally viewed hand`}
              />
            ) : (
              <CameraFeed
                stream={leftStream}
                videoRef={localVideoRef}
                muted
                label={`${playerName(leftId, participantId, participants)}'s live hand camera`}
              />
            )}
            {player && landmarks.length > 0 && state.phase === "countdown" && (
              <svg className="rps-hand-landmarks" viewBox="0 0 100 100" aria-hidden="true">
                {landmarks.map((landmark, index) => (
                  <circle
                    cx={(1 - landmark.x) * 100}
                    cy={landmark.y * 100}
                    r={index === 0 ? 1.5 : 1}
                    key={`${landmark.x}-${landmark.y}`}
                  />
                ))}
              </svg>
            )}
            {countdown && (
              <div className={countdown === "SHOW!" ? "rps-countdown show" : "rps-countdown"}>
                <span>{countdown}</span>
              </div>
            )}
            {(state.phase === "reveal" || state.phase === "finished") && ownThrow?.gesture && (
              <div className={`rps-reveal-gesture ${ownThrow.gesture}`}>
                <span>{gesturePresentation[ownThrow.gesture].icon}</span>
                <b>{gesturePresentation[ownThrow.gesture].label}</b>
              </div>
            )}
            {player && ownThrow?.gesture && (
              <div className="rps-detected-hand" role="status" aria-live="polite">
                <small>YOUR HAND WAS DETECTED AS</small>
                <strong>
                  <span>{gesturePresentation[ownThrow.gesture].icon}</span>
                  {gesturePresentation[ownThrow.gesture].label}
                </strong>
                {ownThrow.confidence !== null && (
                  <i>{Math.round(ownThrow.confidence * 100)}% confidence</i>
                )}
              </div>
            )}
          </div>
        </article>

        <div className="rps-versus" aria-hidden="true">
          <span>VS</span>
          <i />
        </div>

        <article
          className={`rps-player-card rival ${state.roundWinnerId === rivalId ? "round-winner" : ""}`}
        >
          <div className="rps-player-label">
            <span>{playerName(rivalId ?? "", participantId, participants)}</span>
            {rivalThrow?.locked && state.phase === "countdown" && (
              <b>
                <LockKeyhole /> Locked
              </b>
            )}
          </div>
          <div className="rps-camera-stage">
            {rivalId?.startsWith("ai:") ? (
              <div className="rps-bot-camera">
                <Bot />
                <span>Adaptive fair-play bot</span>
                <i />
              </div>
            ) : snapshots[rivalId ?? ""] &&
              (state.phase === "reveal" || state.phase === "finished") ? (
              <img
                src={snapshots[rivalId ?? ""]}
                alt={`${playerName(rivalId ?? "", participantId, participants)}'s locally viewed hand`}
              />
            ) : (
              <CameraFeed
                stream={rivalStream}
                videoRef={rivalVideoRef}
                muted
                label={`${playerName(rivalId ?? "", participantId, participants)}'s live hand camera`}
              />
            )}
            {(state.phase === "reveal" || state.phase === "finished") && rivalThrow?.gesture && (
              <div className={`rps-reveal-gesture ${rivalThrow.gesture}`}>
                <span>{gesturePresentation[rivalThrow.gesture].icon}</span>
                <b>{gesturePresentation[rivalThrow.gesture].label}</b>
              </div>
            )}
          </div>
        </article>
      </section>

      <footer className="rps-control-deck">
        {state.phase === "ready" && player && (
          <div className="rps-camera-consent">
            <div>
              {ready || cameraReady || manualMode ? <Check /> : <Camera />}
              <span>
                <strong>
                  {ready
                    ? "Ready · waiting for the other player"
                    : cameraReady || manualMode
                      ? "Camera ready · starting automatically"
                      : "Open your hand camera"}
                </strong>
                <small>
                  {ready
                    ? "The countdown starts automatically when both players are ready."
                    : "MediaPipe runs here on your device. Topzle does not upload the captured frame."}
                </small>
              </span>
            </div>
            {!ready && !cameraReady && !manualMode && (
              <button
                type="button"
                className="button button-primary"
                onClick={() => setCameraDialogOpen(true)}
                disabled={cameraPending}
              >
                {cameraPending ? <LoaderCircle className="spin" /> : <Camera />}
                {cameraPending ? "Opening…" : "Open camera"}
              </button>
            )}
            {!ready && (cameraReady || manualMode) && (
              <span className="rps-auto-ready" role="status">
                <LoaderCircle className="spin" />
                {pending || modelStatus === "loading" ? "Preparing…" : "Starting timer…"}
              </span>
            )}
            {!ready && !cameraReady && (
              <button
                type="button"
                className="rps-manual-toggle"
                onClick={() => setManualMode((value) => !value)}
              >
                {manualMode
                  ? "Use camera recognition"
                  : "Camera unavailable? Use accessible controls"}
              </button>
            )}
          </div>
        )}

        {state.phase === "countdown" && (
          <div className="rps-recognition-state" aria-live="polite">
            {recognizing ? <LoaderCircle className="spin" /> : <ScanLine />}
            <span>
              <strong>
                {ownThrow?.locked
                  ? `${gesturePresentation[ownThrow.gesture ?? "rock"].label} detected · ${
                      ownThrow.confidence === null
                        ? "throw locked"
                        : `${Math.round(ownThrow.confidence * 100)}% confidence`
                    }`
                  : manualMode
                    ? "Choose only when SHOW appears"
                    : recognitionHint}
              </strong>
              <small>
                {modelStatus === "ready"
                  ? "MediaPipe Hands · on-device"
                  : "Loading on-device hand recognition…"}
              </small>
            </span>
          </div>
        )}

        {manualMode && state.phase === "countdown" && !ownThrow?.locked && (
          <fieldset className="rps-manual-throws">
            <legend className="sr-only">Manual throw controls</legend>
            {(Object.keys(gesturePresentation) as RockPaperScissorsGesture[]).map((gesture) => (
              <button
                type="button"
                onClick={() => submitManualThrow(gesture)}
                disabled={pending || !captureWindowOpen}
                key={gesture}
              >
                <span>{gesturePresentation[gesture].icon}</span>
                {gesturePresentation[gesture].label}
              </button>
            ))}
          </fieldset>
        )}

        {state.phase === "reveal" && (
          <div className="rps-auto-next" role="timer" aria-live="polite">
            <Timer />
            <span>
              <strong>
                {nextRoundSeconds === 0
                  ? "Starting the next throw…"
                  : `Next throw in ${nextRoundSeconds ?? "—"}`}
              </strong>
              <small>Keep your hand in frame · the match continues automatically</small>
            </span>
            <b>{nextRoundSeconds ?? "—"}</b>
          </div>
        )}
        {state.phase === "finished" && <div className="rps-match-actions">{playAgain}</div>}
        {(serverError || modelStatus === "error") && (
          <p className="rps-error" role="alert">
            {serverError ?? "MediaPipe could not load. Manual controls are available."}
          </p>
        )}
      </footer>

      {cameraDialogOpen && (
        <div className="rps-camera-modal-backdrop" role="presentation">
          <button
            type="button"
            className="rps-camera-modal-dismiss"
            aria-label="Close camera setup"
            onClick={() => setCameraDialogOpen(false)}
          />
          <section
            className="rps-camera-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rps-camera-modal-title"
          >
            <header>
              <span className="rps-camera-modal-icon" aria-hidden="true">
                <Camera />
              </span>
              <button
                type="button"
                className="icon-button"
                aria-label="Close camera setup"
                onClick={() => setCameraDialogOpen(false)}
              >
                <X />
              </button>
            </header>
            <div className="rps-camera-modal-body">
              <span>LIVE HAND CAMERA</span>
              <h2 id="rps-camera-modal-title">Ready to play on camera?</h2>
              <p>
                Topzle recognizes rock, paper, or scissors directly on this device. Recognition
                frames are not uploaded or saved; enabled live video is relayed to room players and
                is not recorded.
              </p>
              <ul>
                <li>Keep one hand clearly inside the frame.</li>
                <li>
                  After the camera opens, microphone access is requested separately so declining it
                  never blocks your video.
                </li>
                <li>You can stop the camera from your avatar controls at any time.</li>
                <li>If camera access is unavailable, use the accessible manual controls.</li>
              </ul>
            </div>
            <footer>
              <button
                type="button"
                className="button button-ghost"
                onClick={() => setCameraDialogOpen(false)}
              >
                Not now
              </button>
              <button
                type="button"
                className="button button-primary"
                onClick={() => {
                  onEnableCamera();
                  setCameraDialogOpen(false);
                }}
                disabled={cameraPending}
              >
                {cameraPending ? <LoaderCircle className="spin" /> : <Camera />}
                {cameraPending ? "Opening…" : "Open camera"}
              </button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
}
